import {
  Component,
  computed,
  ContentChildren,
  Input,
  QueryList,
  WritableSignal,
  effect,
  inject,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SidebarMenuService } from '@core/services/sidebar-menu.service';

@Component({
  selector: 'app-sidebar-item',
  standalone: true,
  imports: [],
  templateUrl: './sidebar-item.component.html',
  styleUrl: './sidebar-item.component.scss',
})
export class SidebarItemComponent {
  //Inyeccion de dependencias
  private router = inject(Router);
  private sidebarMenuService = inject(SidebarMenuService);

  //Inputs y Outputs
  @Input() key: string = '';
  // Respaldado por un signal para que `isVisible` (computed) reaccione cuando
  // cambia el título traducido (p. ej. al cambiar de idioma).
  private readonly titleSig = signal('');
  @Input() set title(value: string) {
    this.titleSig.set(value ?? '');
  }
  get title(): string {
    return this.titleSig();
  }
  @Input() iconUrl: string = '';
  @Input() isCollapsable: boolean = false;
  @Input() activeSignal!: WritableSignal<string | null>;
  @Input() route!: string;
  @Input() newBtn: boolean = false;
  @Input() btnLink: string = '';
  @Input() btnTitle: string = '';
  @Input() beta: boolean = false;

  //Variables
  @ContentChildren(SidebarItemComponent)
  nestedItems!: QueryList<SidebarItemComponent>;
  isExpanded: boolean = false;
  isSidebarOpen: boolean = false;

  readonly isSelfSelected = computed(() => {
    const active = this.activeSignal();
    return active !== null && active === this.key;
  });

  readonly isAnyChildSelected = computed(() => {
    const active = this.activeSignal();
    return this.nestedItems?.some((child) => child.key === active) ?? false;
  });

  /** Texto de búsqueda del menú (fuente compartida en el servicio). */
  private readonly menuFilter = this.sidebarMenuService.menuFilter;

  /** Determina si el título de este ítem coincide con la búsqueda. */
  private selfMatchesFilter(query: string): boolean {
    return this.titleSig().toLowerCase().includes(query);
  }

  /** Determina si algún hijo coincide con la búsqueda. */
  private anyChildMatchesFilter(query: string): boolean {
    return (
      this.nestedItems?.some((child) => child.selfMatchesFilter(query)) ?? false
    );
  }

  /**
   * Un ítem es visible cuando no hay búsqueda activa, o cuando su título
   * (o el de alguno de sus hijos) coincide con el texto buscado.
   */
  readonly isVisible = computed(() => {
    const query = this.menuFilter().trim().toLowerCase();
    if (!query) {
      return true;
    }
    return this.selfMatchesFilter(query) || this.anyChildMatchesFilter(query);
  });

  private wasCollapsed = false;
  /** Estado de expansión previo a activar un filtro, para restaurarlo. */
  private preFilterExpanded: boolean | null = null;

  constructor() {
    // Effect para manejar el colapso/expansión del sidebar
    effect(() => {
      const isOpen = this.isSidebarOpen;

      if (!isOpen && this.isCollapsable) {
        // Al colapsar el sidebar: contraer todos los menús verticales
        this.isExpanded = false;
        this.wasCollapsed = true;
      } else if (
        isOpen &&
        this.wasCollapsed &&
        this.isCollapsable &&
        this.hasSelectedChild()
      ) {
        // Al expandir el sidebar: expandir solo si tiene un hijo seleccionado Y venía de estar colapsado
        this.isExpanded = true;
        this.wasCollapsed = false;
      }
    });

    effect(() => {
      this.isSidebarOpen = this.sidebarMenuService.isSidebarOpen();
    });

    // Al buscar, expandir los padres que tengan hijos coincidentes; al
    // limpiar la búsqueda, restaurar el estado de expansión anterior.
    effect(() => {
      const query = this.menuFilter().trim().toLowerCase();

      if (!this.isCollapsable) {
        return;
      }

      if (query && this.anyChildMatchesFilter(query)) {
        if (this.preFilterExpanded === null) {
          this.preFilterExpanded = this.isExpanded;
        }
        this.isExpanded = true;
      } else if (!query && this.preFilterExpanded !== null) {
        this.isExpanded = this.preFilterExpanded;
        this.preFilterExpanded = null;
      }
    });

    // El resaltado del ítem activo se resincroniza con la URL en cada
    // navegación. Antes bastaba con `ngOnInit` porque cada clic recargaba la
    // página entera y el componente se reconstruía; ahora que la navegación es
    // del Router, sin esto el menú se quedaría marcando la pantalla anterior al
    // llegar por otro camino (tarjeta del dashboard, deep link, botón volver).
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.checkCurrentRoute());
  }

  ngOnInit(): void {
    this.checkCurrentRoute();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isSidebarOpen']) {
      if (this.isAnyChildSelected() && this.isSidebarOpen && !this.isExpanded) {
        this.onClick(new MouseEvent('click'));
      } else if (
        !this.isAnyChildSelected() &&
        this.isSidebarOpen &&
        this.isExpanded
      ) {
        this.isExpanded = false;
      }
    }
  }

  ngAfterContentInit(): void {
    // Verifica si un hijo está activo cuando se carga el componente
    if (this.hasSelectedChild()) {
      this.isExpanded = true;
    }
  }

  /**
   * Navega con el Router en vez de dejar que el navegador siga el `href`.
   *
   * El `href` se conserva en la plantilla para que Cmd/Ctrl+clic y "abrir en
   * pestaña nueva" sigan funcionando, pero el clic normal se cancela: sin esto
   * cada opción del menú provocaba una recarga completa de la aplicación
   * (re-bootstrap de Angular, refetch de empresas, reconexión del hub de
   * notificaciones y pérdida de todo el estado en memoria).
   */
  onClick(event: MouseEvent): void {
    event.stopPropagation();

    // Los grupos solo pliegan y despliegan; nunca navegan.
    if (this.isCollapsable) {
      event.preventDefault();
      this.isExpanded = !this.isExpanded;
      return;
    }

    // Clic con modificador o botón secundario: lo maneja el navegador con el
    // `href` nativo (nueva pestaña / nueva ventana).
    if (this.isModifiedClick(event)) {
      return;
    }

    event.preventDefault();

    // Ítems que todavía no tienen pantalla (`route="#"`). Como `index.html`
    // declara `<base href="/">`, un `href="#"` NO resuelve al documento actual
    // sino a la raíz: sin este corte, el clic se llevaba al usuario a `/`.
    if (!this.hasDestination()) {
      return;
    }

    this.activeSignal.set(this.key);
    // `navigateByUrl` y no `navigate([...])` para que una ruta con query string
    // —p. ej. `/dashboard?eje=expo`— se parsee como parámetro real y no como un
    // segmento codificado. Mismo criterio que `goToCreate`.
    this.router.navigateByUrl(this.route);
  }

  /** Clic que el navegador debe resolver por su cuenta (nueva pestaña/ventana). */
  private isModifiedClick(event: MouseEvent): boolean {
    return (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    );
  }

  /** El ítem apunta a una pantalla real; los pendientes de migrar usan `#`. */
  private hasDestination(): boolean {
    return !!this.route && this.route !== '#';
  }

  // Verifica si algún hijo está seleccionado actualmente
  private hasSelectedChild(): boolean {
    if (!this.activeSignal) {
      return false;
    }
    return (
      this.nestedItems?.some((child) => child.key === this.activeSignal()) ??
      false
    );
  }

  checkCurrentRoute() {
    // `activeSignal` llega por property binding, que Angular aplica en la
    // detección de cambios y NO al crear el elemento (a diferencia de `key`,
    // `route` e `iconUrl`, que son atributos estáticos). El `NavigationEnd` del
    // constructor puede caer en esa rendija: el ítem ya tiene `route` pero
    // todavía no la señal, y esto reventaba con "Cannot read properties of
    // undefined (reading 'set')". El binding llega enseguida y `ngOnInit`
    // vuelve a evaluar la ruta, así que saltarse esta pasada no pierde nada.
    if (!this.activeSignal) {
      return;
    }

    // Sin destino no hay nada que resaltar. Además protege del caso `route=''`,
    // que haría `startsWith('')` verdadero para cualquier URL y dejaría el
    // ítem marcado siempre.
    if (!this.hasDestination()) {
      return;
    }

    const currentRoute = this.router.url;
    if (currentRoute.startsWith(this.route)) {
      this.activeSignal.set(this.key);
    } else if (this.activeSignal() === this.key) {
      // La URL ya no es de este ítem (p. ej. un detalle sin ítem propio en el
      // menú): se quita el resaltado en vez de dejar marcada la pantalla anterior.
      this.activeSignal.set(null);
    }
  }

  goToCreate(event: MouseEvent) {
    event.stopPropagation();
    // Evita que el ancla contenedor (`href="{{ route }}"`) dispare su
    // navegación nativa y sobreescriba el destino del botón "+".
    event.preventDefault();
    // `navigateByUrl` (en vez de `navigate([...])`) para que un `btnLink` con
    // query string —p. ej. `/mis-tareas?nueva=1`— se parsee como parámetro real
    // y no como un segmento de ruta codificado.
    this.router.navigateByUrl(this.btnLink);
  }
}
